import useGeneralApi from "views/Settings/General/hooks/useGeneralApi";
import useNotification from "utils/hooks/useNotification";
import useFooterStore from "views/hooks/useFooterStore";
import useCoreStore from "views/hooks/useCoreStore";
import { ApolloQueryResult } from "@apollo/client";
import { getFormError } from "utils/getFormError";
import { useEffect } from "react";
import { Form } from "antd";
import { generateIntracomVatNumber } from "utils/vatUtils";

export const useIdentityForm = (
  mainAddress: any,
  nextButton?: () => void,
  isSetup?: boolean,
  refetch?: (
    variables?: Partial<GetVarTypeID> | undefined,
  ) => Promise<ApolloQueryResult<GetCompanyResponse>>,
) => {
  const [form] = Form.useForm();
  const { updateCompany, updateCompanyAddress, createCompanyAddress } = useGeneralApi();
  const { notificationSuccessfullySaved } = useNotification();
  const { setHandleOkSaveButton } = useFooterStore();
  const setSaveLoading = useFooterStore(state => state.setSaveLoading);
  const companyData = useCoreStore(state => state.companyData);
  const companySelected = useCoreStore(state => state.companySelected);
  const companyId = companySelected?.id;

  useEffect(() => {
    if (companyData) {
      form.setFields([
        {
          name: "legalStructure",
          value: companyData?.legalStructure && {
            label: `${companyData.legalStructure.name} - ${companyData.legalStructure.code}`,
            value: companyData.legalStructure.code,
          },
        },
        {
          name: "legalCategory",
          value: companyData?.legalCategory && {
            label: `${companyData.legalCategory.name} - ${companyData.legalCategory.code}`,
            value: companyData.legalCategory.code,
          },
        },
        {
          name: "businessActivity",
          value: companyData?.businessActivity && {
            label: `${companyData.businessActivity.name} - ${companyData.businessActivity.code}`,
            value: companyData.businessActivity.code,
          },
        },
        {
          name: "name",
          value: companyData?.name,
        },
        {
          name: "displayName",
          value: companyData?.displayName,
        },
        {
          name: "rcs",
          value: companyData?.rcs,
        },
        {
          name: "vatNumber",
          value: companyData?.vatNumber,
        },
        {
          name: "capitalAmount",
          value: companyData?.capitalAmount
            ? Number(companyData?.capitalAmount).toFixed(2)
            : companyData?.capitalAmount,
        },
        {
          name: "euVatNumber",
          value: companyData?.euVatNumber,
        },
        {
          name: "email",
          value: companyData?.email,
        },
        {
          name: "phone1",
          value: companyData?.phone1,
        },
        {
          name: "phone2",
          value: companyData?.phone2,
        },
        {
          name: "fax",
          value: companyData?.fax,
        },
      ]);
    }
  }, [companyData, form]);

  useEffect(() => {
    if (mainAddress) {
      form.setFields([
        {
          name: "country",
          value: {
            label: mainAddress?.country?.name,
            value: mainAddress?.country?.code,
          },
        },
        {
          name: "city",
          value: {
            label: mainAddress.city,
            value: mainAddress.city,
          },
        },
        {
          name: "postalCode",
          value: {
            label: `${mainAddress.postalCode}`,
            value: `${mainAddress.postalCode}`,
          },
        },
        {
          name: "addressId",
          value: mainAddress?.id,
        },
        {
          name: "addressNr",
          value: mainAddress?.addressNr,
        },
        {
          name: "addressBtq",
          value: mainAddress?.addressBtq,
        },
        {
          name: "address1",
          value: mainAddress?.address1,
        },
        {
          name: "address2",
          value: mainAddress?.address2,
        },
      ]);
    }
  }, [mainAddress, form]);

  const handleVatNumberChange = (vatNumberValue: string) => {
    if (vatNumberValue && !companyData?.euVatNumber && isSetup) {
      const intracomVatNumber = generateIntracomVatNumber(vatNumberValue);
      form.setFieldsValue({
        euVatNumber: intracomVatNumber,
      });
    }
  };

  const onFinish = async (values: any) => {
    const companyContactVariables = {
      email: values?.email,
      phone1: values?.phone1,
      phone2: values?.phone2,
      fax: values?.fax,
    };

    const companyInfoVariables = {
      input: {
        id: companyId,
        name: values.name,
        displayName: values.displayName,
        rcs: values.rcs,
        euVatNumber: values.euVatNumber,
        businessActivity: values?.businessActivity?.value,
        capitalAmount: values?.capitalAmount,
        legalStructure: values?.legalStructure?.value,
        legalCategory: values?.legalCategory?.value,
        ...companyContactVariables,
      },
    };

    const addressMutation = values?.addressId
      ? updateCompanyAddress({
          variables: {
            input: {
              companyId,
              id: values.addressId,
              address1: values.address1,
              address2: values.address2,
              postalCode: values.postalCode?.value || "",
              addressType: "main",
              addressNr: values.addressNr,
              addressBtq: values.addressBtq,
              countryId: values?.country?.value || "FR",
              city: values.city?.value,
            },
          },
        })
      : createCompanyAddress({
          variables: {
            input: {
              companyId,
              address1: values.address1,
              address2: values.address2,
              postalCode: values.postalCode?.value || "",
              addressType: "main",
              addressNr: values.addressNr,
              addressBtq: values.addressBtq,
              countryId: values?.country?.value || "FR",
              city: values.city?.value,
            },
          },
        });

    const [responseUpdateCompanyInvoice, responseAddress] = await Promise.all([
      updateCompany({ variables: companyInfoVariables }),
      addressMutation,
    ]);

    const addressData = responseAddress.data as
      | UpdateCompanyAddressMutationResponse
      | CreateCompanyAddressMutationResponse
      | undefined;
    const errors = [
      ...(responseUpdateCompanyInvoice.data?.updateCompany?.errors || []),
      ...((addressData as UpdateCompanyAddressMutationResponse)?.updateAddress?.errors ||
        (addressData as CreateCompanyAddressMutationResponse)?.createAddress?.errors ||
        []),
    ];

    setSaveLoading(false);

    if (errors.length > 0) {
      const fieldsData = getFormError(errors, values);
      form.setFields(fieldsData);
      setHandleOkSaveButton(false);
    } else {
      notificationSuccessfullySaved("Identity");
      setHandleOkSaveButton(false);
      nextButton?.();
      refetch?.();
    }
  };

  const onValuesChange = (changedValues: any, allValues: any) => {
    if (changedValues?.postalCode) {
      form.setFieldsValue({
        ...allValues,
        city: {
          label: changedValues?.postalCode.data.city,
          value: changedValues?.postalCode.data.city,
          data: null,
        },
      });
    }

    if (changedValues?.city?.value && !allValues.postalCode) {
      form.setFieldsValue({
        ...allValues,
        postalCode: {
          label: `${changedValues?.city.data.postalCode} - ${changedValues?.city.data.city}`,
          value: `${changedValues?.city.data.postalCode} - ${changedValues?.city.data.city}`,
          data: null,
        },
      });
    }
  };

  return {
    form,
    onFinish,
    onValuesChange,
    handleVatNumberChange,
  };
};
